import React, { useState } from 'react';
import './styles.css';
import boardCertifiedImg from '../../images/star.png';
import infoCircleIcon from '../../images/infoCircle.svg';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { formatYesNo } from '../../utils/yesNoDisplay';
import HoverTooltip from '../hoverTooltip/hoverTooltip';
import GroupAffiliationsModal from '../groupAffiliationsModal/groupAffiliationsModal';

// provider?.specialProviderType can be a list of up to two values (e.g. an AI/AN
// provider designation and a separate type) — each needs its own distinct tooltip
// heading/body rather than sharing the single hardcoded "AI/AN provider" tooltip that
// used to be shown regardless of which value(s) were present.
const SPECIAL_PROVIDER_TYPE_TOOLTIPS = {
  'AI/AN': {
    heading: 'AI/AN provider',
    body: 'This provider is a member of a federally recognized American Indian Tribe. Or they are an Alaskan Native.'
  },
  'CCBHC': {
    heading: 'CCBHC provider',
    body: "These providers offer a range of services for mental health. Treatment for substance use disorders is part of these services. They treat anyone. It doesn't matter what a person's age or health condition is. Their ability to pay also doesn't affect their treatment."
  }
};

const renderSpecialties = (providerSpecialty) => {
  if (!providerSpecialty || providerSpecialty.length === 0) {
    return { certifiedNames: [], notCertifiedNames: [] };
  }

  const certifiedNames = [];
  const notCertifiedNames = [];

  providerSpecialty.forEach((spec) => {
    if (!spec?.specialtyName) return;
    if (spec.boardCertifiedFlag === 'Yes') {
      certifiedNames.push(spec.specialtyName);
    } else {
      notCertifiedNames.push(spec.specialtyName);
    }
  });

  return { certifiedNames, notCertifiedNames };
};

// Helper function to convert string to Title Case (Camel Case)
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Ethnicity/race values that should be treated as "no data" and displayed as
// "None found" instead of their raw (unhelpful/placeholder) API text.
const UNKNOWN_ETHNICITY_VALUES = [
  'UNKNOWN',
  'I CHOOSE NOT TO ANSWER',
  'NULL',
  'NO ETHNICITY',
  'NOT PROVIDED',
  'NOT APPLICABLE',
];

const getEthnicityDisplay = (ethnicity) => {
  if (!ethnicity) return 'None found';
  // Normalize case (handles UPPER, lower, or Mixed Case values from the API)
  // and collapse extra whitespace before comparing against known placeholders.
  const normalized = ethnicity.trim().replace(/\s+/g, ' ').toUpperCase();
  if (UNKNOWN_ETHNICITY_VALUES.includes(normalized)) return 'None found';
  return toTitleCase(ethnicity);
};

const ProviderDetails = ({
  provider,
  providerSpecialty,
  isExpanded = true
}) => {
  const { isProviderDetailsFieldEnabled } = usePlanFeatures();
  // Group/Hospital affiliations tooltips should only be reachable by keyboard while
  // this "Provider details" accordion section is actually expanded — the shared
  // .expandable-content accordion collapses via max-height:0/overflow:hidden rather
  // than display:none (see results/styles.css), so its contents (including these
  // tooltip triggers) stayed in the tab order and could be silently focused while
  // still visually hidden/collapsed.2
  const affiliationTooltipTabIndex = isExpanded ? 0 : -1;

  // "Show all group affiliations" popup — only used when there are more than 5 group
  // affiliations to display (see the group affiliations render below), so the inline
  // text doesn't grow unbounded for providers with many affiliations.
  const [showAllAffiliationsModal, setShowAllAffiliationsModal] = useState(false);
  // Same "show all" treatment for Hospital affiliations and Special skills, each with
  // its own independent modal open state and reusing the same generic GroupAffiliationsModal
  // component (just with a different title/list per section).
  const [showAllHospitalAffiliationsModal, setShowAllHospitalAffiliationsModal] = useState(false);
  const [showAllSpecialSkillsModal, setShowAllSpecialSkillsModal] = useState(false);
  // Same "show all" treatment for board-certified / not-board-certified specialties —
  // matches the exact truncate-at-3 + "Show all..." link + GroupAffiliationsModal
  // pattern used above for Group affiliations, Hospital affiliations, and Special
  // skills, so a provider with a long specialty list doesn't grow this row unbounded.
  const [showAllBoardCertifiedModal, setShowAllBoardCertifiedModal] = useState(false);
  const [showAllNotBoardCertifiedModal, setShowAllNotBoardCertifiedModal] = useState(false);

  // Renders a comma-separated list inline, truncating to the first `maxInline`
  // entries (default 5) with a "Show all {label}" link + modal (reusing
  // GroupAffiliationsModal) when there are more than that — the same
  // truncation/modal pattern as Group affiliations/Hospital affiliations/Special
  // skills, extracted here since board-certified and not-board-certified
  // specialties both need it too.
  const renderTruncatedList = (names, { emptyText, linkText, modalTitle, isOpen, onOpen, onClose, tabIndex, maxInline = 5 }) => {
    if (!names || names.length === 0) {
      return <span>{emptyText}</span>;
    }
    if (names.length > maxInline) {
      return (
        <>
          {names.slice(0, maxInline).join(', ')}{', '}
          <button
            type="button"
            className="show-all-affiliations-link"
            onClick={onOpen}
            tabIndex={tabIndex}
          >
            {linkText}
          </button>
          <GroupAffiliationsModal
            isOpen={isOpen}
            onClose={onClose}
            affiliations={names}
            title={modalTitle}
          />
        </>
      );
    }
    return names.join(', ');
  };

  return (
    <div className="frameDetail">
            {(isProviderDetailsFieldEnabled('gender') || isProviderDetailsFieldEnabled('ethnicity')) && (
              <div className="group">
                <div className="div-8">
                    {isProviderDetailsFieldEnabled('gender') && (
                      <div className="div-9">
                          <span className="text-wrapper">Provider gender: </span>
                          <p className="text-wrapper-3">{provider?.provSex ? (provider.provSex === 'F' ? 'Female' : 'Male') : 'None found'}</p>
                      </div>
                    )}

                    {isProviderDetailsFieldEnabled('ethnicity') && (
                      <div className="div-9">
                          <span className="text-wrapper">Provider race/ethnicity: </span>
                          <p className="text-wrapper-3">{getEthnicityDisplay(provider?.ethnicity)}</p>
                      </div>
                    )}
                </div>
              </div>
            )}

            <div className="div-2">
                <div className="div-3">
                    <div className="div-4">
                        {isProviderDetailsFieldEnabled('boardCertifiedSpecialties') && (
                          <div className="frameDetail-wrapper">
                              <div className="div-5">
                                  <p className="p">
                                  <img className="img mr10" src={boardCertifiedImg} alt="" />
                                  <span className="text-wrapper mr5">
                                      Board-certified specialties: 
                                  </span>                                 

                                  <span className="span">
                                      {renderTruncatedList(renderSpecialties(providerSpecialty).certifiedNames, {
                                          emptyText: 'No board certification found',
                                          linkText: 'Show all board-certified specialties',
                                          modalTitle: 'Board-certified specialties',
                                          isOpen: showAllBoardCertifiedModal,
                                          onOpen: () => setShowAllBoardCertifiedModal(true),
                                          onClose: () => setShowAllBoardCertifiedModal(false),
                                          tabIndex: affiliationTooltipTabIndex
                                      })}
                                  </span>
                              </p>
                              </div>
                          </div>
                        )}

                        {isProviderDetailsFieldEnabled('specialtiesNotBoardCertified') && (
                          <div className="specialties-not-wrapper">
                              <p className="p">
                                  <span className="text-wrapper">
                                      Specialties not board certified:{" "}
                                  </span>

                                  <span className="span">
                                      {renderTruncatedList(renderSpecialties(providerSpecialty).notCertifiedNames, {
                                          emptyText: 'None found',
                                          linkText: 'Show all specialties not board certified',
                                          modalTitle: 'Specialties not board certified',
                                          isOpen: showAllNotBoardCertifiedModal,
                                          onOpen: () => setShowAllNotBoardCertifiedModal(true),
                                          onClose: () => setShowAllNotBoardCertifiedModal(false),
                                          tabIndex: affiliationTooltipTabIndex
                                      })}
                                  </span>
                              </p>
                          </div>
                        )}
                    </div>
                    <div className="tool-tip">
                        <div className="div-7" />
                    </div>
                </div>

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                <div className="group">
                    <div className="div-8">
                        {isProviderDetailsFieldEnabled('groupAffiliations') && (
                          <div className="div-9">
                            <div className="label">
                                <div className="text-wrapper-2">Group affiliations</div>

                                <HoverTooltip
                                  heading="Group affiliations"
                                  body="Shows what practice or group a provider takes part in. They can provide care as part of the group. They also share practice resources."
                                  tabIndex={affiliationTooltipTabIndex}
                                >
                                  <img src={infoCircleIcon} alt="" style={{ width: '24px', height: '24px', cursor: 'help' }} />
                                </HoverTooltip>
                            </div>

                            <p className="text-wrapper-3"> 
                                {(() => {
                                    const groupAffiliationNames = provider?.affiliations && provider.affiliations.length > 0
                                        ? provider.affiliations
                                            .filter(aff => aff.affilType === 'GROUP' || aff.affilType === 'DIRECT')
                                            .map(aff => aff.affiliateName)
                                            .filter(Boolean)
                                        : [];

                                    if (groupAffiliationNames.length === 0) {
                                        return 'None found';
                                    }

                                    if (groupAffiliationNames.length > 3) {
                                        return (
                                            <>
                                                {groupAffiliationNames.slice(0, 3).join(', ')}{', '}
                                                <button
                                                    type="button"
                                                    className="show-all-affiliations-link"
                                                    onClick={() => setShowAllAffiliationsModal(true)}
                                                    tabIndex={affiliationTooltipTabIndex}
                                                >
                                                    Show all group affiliations
                                                </button>
                                                <GroupAffiliationsModal
                                                    isOpen={showAllAffiliationsModal}
                                                    onClose={() => setShowAllAffiliationsModal(false)}
                                                    affiliations={groupAffiliationNames}
                                                    title="Group affiliations"
                                                />
                                            </>
                                        );
                                    }

                                    return groupAffiliationNames.join(', ');
                                })()}
                            </p>
                          </div>
                        )}

                        {isProviderDetailsFieldEnabled('hospitalAffiliations') && (
                          <div className="div-9">
                            <div className="label">
                                <div className="text-wrapper-2">Hospital affiliations</div>

                                <HoverTooltip
                                  heading="Hospital affiliations"
                                  body="Shows what hospital network a provider or group takes part in. They provide care as part of the network. They also share network resources."
                                  tabIndex={affiliationTooltipTabIndex}
                                >
                                  <img src={infoCircleIcon} alt="" style={{ width: '24px', height: '24px', cursor: 'help' }} />
                                </HoverTooltip>
                            </div>

                            <p className="text-wrapper-3">
                                {(() => {
                                    const hospitalAffiliationNames = provider?.affiliations && provider.affiliations.length > 0
                                        ? provider.affiliations.filter(aff => aff.affilType === 'HOSP STAFF')
                                            .map(aff => aff.affiliateName)
                                            .filter(Boolean)
                                        : [];

                                    if (hospitalAffiliationNames.length === 0) {
                                        return 'None found';
                                    }

                                    if (hospitalAffiliationNames.length > 3) {
                                        return (
                                            <>
                                                {hospitalAffiliationNames.slice(0, 3).join(', ')}{', '}
                                                <button
                                                    type="button"
                                                    className="show-all-affiliations-link"
                                                    onClick={() => setShowAllHospitalAffiliationsModal(true)}
                                                    tabIndex={affiliationTooltipTabIndex}
                                                >
                                                    Show all hospital affiliations
                                                </button>
                                                <GroupAffiliationsModal
                                                    isOpen={showAllHospitalAffiliationsModal}
                                                    onClose={() => setShowAllHospitalAffiliationsModal(false)}
                                                    affiliations={hospitalAffiliationNames}
                                                    title="Hospital affiliations"
                                                />
                                            </>
                                        );
                                    }

                                    return hospitalAffiliationNames.join(', ');
                                })()}
                            </p>
                          </div>
                        )}
                    </div>

                </div>

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                {isProviderDetailsFieldEnabled('specialTraining') && (
                    <div className="group-wrapper">
                        <div className="group-2">
                            <p className={`special-training${(isProviderDetailsFieldEnabled('specialProviderType') && provider?.specialProviderTypes?.length > 0) ? ' splProvType' : ''}`}>
                                <span className="text-wrapper">Special skills:</span>

                                <span className="span">
                                    {' '}
                                    {(() => {
                                        const specialSkillNames = typeof provider?.specialTraining === 'string' && provider.specialTraining.trim().length > 0
                                            ? provider.specialTraining.split(',').map(s => s.trim()).filter(Boolean)
                                            : [];

                                        if (specialSkillNames.length === 0) {
                                            return 'None found';
                                        }

                                        if (specialSkillNames.length > 3) {
                                            return (
                                                <>
                                                    {specialSkillNames.slice(0, 3).join(', ')}{', '}
                                                    <button
                                                        type="button"
                                                        className="show-all-affiliations-link"
                                                        onClick={() => setShowAllSpecialSkillsModal(true)}
                                                        tabIndex={affiliationTooltipTabIndex}
                                                    >
                                                        Show all special skills
                                                    </button>
                                                    <GroupAffiliationsModal
                                                        isOpen={showAllSpecialSkillsModal}
                                                        onClose={() => setShowAllSpecialSkillsModal(false)}
                                                        affiliations={specialSkillNames}
                                                        title="Special skills"
                                                    />
                                                </>
                                            );
                                        }

                                        return specialSkillNames.join(', ');
                                    })()}
                                </span>
                            </p>

                            {isProviderDetailsFieldEnabled('specialProviderType') && provider?.specialProviderTypes?.length > 0 && (
                                <p className="special-provider-type">
                                    <span className="text-wrapper">Special Provider Type:</span>

                                    {(Array.isArray(provider.specialProviderTypes)
                                        ? provider.specialProviderTypes
                                        : [provider.specialProviderTypes]
                                    ).filter(Boolean).map((typeValue, idx, arr) => {
                                        const tooltipInfo = typeValue.includes('CCBHC')
                                            ? SPECIAL_PROVIDER_TYPE_TOOLTIPS['CCBHC']
                                            : typeValue.includes('American Indian')
                                            ? SPECIAL_PROVIDER_TYPE_TOOLTIPS['AI/AN']
                                            : SPECIAL_PROVIDER_TYPE_TOOLTIPS[typeValue] || {
                                                heading: typeValue,
                                                body: ''
                                            };
                                        return (
                                            <span className="span" key={typeValue}>
                                                {' '}{typeValue}
                                                <HoverTooltip
                                                    heading={tooltipInfo.heading}
                                                    body={tooltipInfo.body}
                                                >
                                                    <img src={infoCircleIcon} alt="" style={{ width: '16px', height: '16px', cursor: 'help' }} />
                                                </HoverTooltip>
                                                {idx < arr.length - 1 ? ',' : ''}
                                            </span>
                                        );
                                    })}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                {isProviderDetailsFieldEnabled('cshcsProvider') && (
                    <div className="group-wrapper cshcs-wrapper">
                        <div className="group-2">
                            <p className="cshcs-provider">
                                <span className="text-wrapper">Children&rsquo;s special health care services (CSHCS) provider:</span>

                                <span className="span">
                                    {' '}
                                    {formatYesNo(provider?.cshcsProvider)}
                                </span>
                            </p>
                        </div>
                    </div>
                )}

                <div className="card-section-divider" role="separator" aria-hidden="true" />

                <div className="div-10">

                  {isProviderDetailsFieldEnabled('provId') && (
                      <p className="p">
                          <span className="text-wrapper">Provider ID: </span>

                          <span className="span" translate='no'>{(provider?.provId && provider.provId !== '') || (provider?.providerId && provider.providerId !== '') ? (provider.provId || provider.providerId) : 'None found'}</span>
                      </p>
                    )}
                    
                    {isProviderDetailsFieldEnabled('npi') && (
                      <p className="p">
                          <span className="text-wrapper">NPI ID: </span>

                          <span className="span" translate='no'>{provider?.npi && provider.npi !== '' ? provider.npi : 'None found'}</span>
                      </p>
                    )}

                    {isProviderDetailsFieldEnabled('stateLicenseNumber') && (
                      <p className="p">
                          <span className="text-wrapper">State license number:</span>

                          <span className="span" translate='no'>{(provider?.stateLicenseNumber && provider.stateLicenseNumber !== '') ? (provider.stateLicenseNumber) : 'None found'}</span>
                      </p>
                    )}
                </div>
            </div>
        </div>
  );
};

export default ProviderDetails;
